import { useSearchParams } from 'react-router';
import { DEFAULT_USAGE_MEASURE, USAGE_MEASURE_VALUES, type UsageMeasure, type UsageView } from './usage.type';

/**
 * URL-synced token-measure control for the Usage tab, held in the `measure`
 * param so it survives navigation like the rest of the page's filter state.
 * The default measure omits the param entirely (keeping shared URLs clean),
 * and an unrecognized hand-edited value falls back to the default rather than
 * charting garbage.
 *
 * @returns {{ measure: UsageMeasure, setMeasure: (next: UsageMeasure) => void }}
 */
const useUsageMeasure = (): {
  measure: UsageMeasure;
  setMeasure: (next: UsageMeasure) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('measure');
  const measure: UsageMeasure = raw && USAGE_MEASURE_VALUES.has(raw) ? (raw as UsageMeasure) : DEFAULT_USAGE_MEASURE;

  const setMeasure = (next: UsageMeasure) => {
    const copy = new URLSearchParams(searchParams);
    if (next === DEFAULT_USAGE_MEASURE) copy.delete('measure');
    else copy.set('measure', next);
    setSearchParams(copy, { replace: true });
  };

  return { measure, setMeasure };
};

/**
 * URL-synced line/bar/table toggle for the Token-usage view, held in the
 * `usage_view` param. This is the toggle rendered in the chart header (not the
 * sidebar), independent of the sidebar `view` selector that chose the
 * Token-usage view in the first place. Defaults to the line chart, omitting
 * the param entirely so shared URLs stay clean; an unknown hand-edited value
 * falls back to the line chart.
 *
 * @returns {{ usageView: UsageView, setUsageView: (next: UsageView) => void }}
 */
const useUsageView = (): {
  usageView: UsageView;
  setUsageView: (next: UsageView) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('usage_view');
  const usageView: UsageView = raw === 'bar' || raw === 'table' ? raw : 'line';

  const setUsageView = (next: UsageView) => {
    const copy = new URLSearchParams(searchParams);
    if (next === 'line') copy.delete('usage_view');
    else copy.set('usage_view', next);
    setSearchParams(copy, { replace: true });
  };

  return { usageView, setUsageView };
};

// The date-window, allocation-type, provider, view, and hidden-family filters
// are identical to Model Comparison's and are all URL-param backed, so the
// Usage tab reuses those hooks verbatim rather than duplicating them.
export {
  useModelComparisonAllocationType,
  useModelComparisonHiddenFamilies,
  useModelComparisonParams,
  useModelComparisonProviderFilter,
  useModelComparisonView,
} from '../model-comparison/model-comparison.hooks';
export { useUsageMeasure, useUsageView };
